/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable consistent-return */
/* eslint-disable no-restricted-syntax */
import {
  ApolloClient,
  createHttpLink,
  from,
  InMemoryCache,
  NormalizedCacheObject,
  Observable,
  split,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'

import { AUTH_ERROR_CODES } from '@/constants'
import { tokenManager } from '@/services/TokenManager/TokenManager'

let apolloClient: ApolloClient<NormalizedCacheObject> | null

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        isLoggedIn: {
          read() {
            return !!tokenManager.getAccessToken()
          },
        },
      },
    },
  },
})

const httpLink = createHttpLink({
  uri: import.meta.env.VITE_API_URL,
})

const httpAuthLink = createHttpLink({
  uri: `${import.meta.env.VITE_API_URL}/system`,
})

const authLink = setContext(async (_, { headers }) => {
  // Decode JWT exp and refresh before sending if expired/about to expire.
  // This prevents TOKEN_EXPIRED errors from ever reaching Directus.
  const token = (await tokenManager.getValidAccessToken()) || ''

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  }
})

/**
 * Backward-compatible export for Axios interceptor and other consumers.
 * Delegates entirely to TokenManager's single-gate refresh.
 */
export const refreshToken = () => tokenManager.refreshToken()

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        if (AUTH_ERROR_CODES.has(err.extensions.code as string)) {
          // ENG-1633: if the manager is already torn down (logout / redirect
          // in progress), don't trigger a refresh — it would throw
          // "TokenManager destroyed" and surface as an uncaught error. Let the
          // original auth error propagate to the (unmounting) query instead.
          if (tokenManager.isDestroyed()) {
            return
          }
          // Bug #3 fix: check per-operation retry budget before refreshing
          if (tokenManager.hasExhaustedRetryBudget(operation)) {
            return
          }
          tokenManager.markRetryAttempted(operation)

          return new Observable(observer => {
            tokenManager
              .refreshToken()
              .then(newAccessToken => {
                operation.setContext(({ headers }: any) => ({
                  headers: {
                    ...headers,
                    Authorization: `Bearer ${newAccessToken}`,
                  },
                }))
                forward(operation).subscribe(observer)
              })
              .catch(refreshError => {
                // Bug #1 fix: actually call observer.error() instead of .bind()
                observer.error(refreshError)
              })
          })
        }
      }
    }

    // Directus returns HTTP 403 with INVALID_TOKEN for corrupted JWTs.
    // Apollo puts non-200 responses on networkError, not graphQLErrors,
    // so we need to check the response body for auth error codes here too.
    if (networkError && 'statusCode' in networkError) {
      const { statusCode } = networkError as { statusCode: number }
      if (statusCode === 401 || statusCode === 403) {
        const { result } = networkError as unknown as {
          result?: { errors?: Array<{ extensions?: { code?: string } }> }
        }
        const hasAuthError = result?.errors?.some(e =>
          AUTH_ERROR_CODES.has(e.extensions?.code ?? '')
        )

        if (hasAuthError) {
          // ENG-1633: skip refresh after teardown (see graphQLErrors branch).
          if (tokenManager.isDestroyed()) {
            return
          }
          if (tokenManager.hasExhaustedRetryBudget(operation)) {
            return
          }
          tokenManager.markRetryAttempted(operation)

          return new Observable(observer => {
            tokenManager
              .refreshToken()
              .then(newAccessToken => {
                operation.setContext(({ headers }: any) => ({
                  headers: {
                    ...headers,
                    Authorization: `Bearer ${newAccessToken}`,
                  },
                }))
                forward(operation).subscribe(observer)
              })
              .catch(refreshError => {
                observer.error(refreshError)
              })
          })
        }
      }

      // eslint-disable-next-line no-console
      console.log(`[Network error]: ${networkError}`)
    }
  }
)

// Docs: https://www.apollographql.com/docs/react/api/link/apollo-link-retry/
const MAX_RETRY_ATTEMPTS = 5
const retryLink = new RetryLink({
  attempts: (count, operation, error) => {
    // This retry is implemented for the AttorneyByEmail query since it is an
    // important query in the create account flow and there is a very small chance
    // it requires a retry due to multiple updates that may have not finished in the BE.
    if (operation.operationName === 'AttorneyByEmail') {
      // This second condition is to retry the request with a new token since the first
      // one might be invalid.
      if (!!error && count === 2) {
        if (tokenManager.hasExhaustedRetryBudget(operation)) {
          return false
        }
        tokenManager.markRetryAttempted(operation)
        return tokenManager
          .refreshToken()
          .then(newToken => !!newToken && count < MAX_RETRY_ATTEMPTS)
          .catch(() => false)
      }
      return !!error && count < MAX_RETRY_ATTEMPTS
    }
    return false
  },
  delay: {
    initial: 500,
    max: 3000,
    jitter: true,
  },
})

const link = from([
  errorLink,
  retryLink,
  split(
    definition => definition.getContext().clientName === 'auth',
    // For authentication related requests or anything on the */graphql/system endpoint (e.g. login, createAccount)
    authLink.concat(httpAuthLink),
    // For regular requests to */graphql
    authLink.concat(httpLink)
  ),
])

const createApolloClient = () =>
  new ApolloClient({
    cache,
    link,
    connectToDevTools: process.env.NODE_ENV === 'development',
  })

const initApollo = () => {
  const apolloClientInstance = apolloClient ?? createApolloClient()

  if (!apolloClient) {
    apolloClient = apolloClientInstance

    // `isLoggedIn` reads localStorage, which the cache cannot see change, so
    // its first result is memoized. When a rejected refresh drops the session
    // on /signIn (no page reload), evict the field so every IS_LOGGED_IN
    // watcher, including AuthRoute, re-reads it as false and renders the
    // sign-in form instead of forwarding into an app that can no longer
    // authenticate (ENG-1932). Registered once, alongside the client.
    tokenManager.onSessionCleared(() => {
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'isLoggedIn' })
      cache.gc()
    })
  }

  return apolloClientInstance
}

export default initApollo
